import React, { useState, useEffect } from 'react';
import { Globe, Medal, Award, Shield, Users, Briefcase, MapPin, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import about from '../assets/about2.jpg'
import story from '../assets/story.jpg'
import trading from '../assets/trading.jpg'
import shipping from '../assets/shipping.jpg'
import quality from '../assets/quality.jpg'
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const AboutUs = () => {
  const [scrolled, setScrolled] = useState(false);
  
  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <>
      <Header />
      
      <div className="flex flex-col min-h-screen bg-gray-50">
        {/* Page Header */}
        <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 mb-10 md:mb-0">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  About <span className="text-yellow-400">PVG Import Export</span>
                </h1>
                <p className="text-lg text-gray-300 mb-8">
                  A global leader in precious metals trading and industry expertise and an unwavering commitment to excellence.
                </p>
              </div>
              <div className="md:w-auto relative">
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 p-1 rounded-lg shadow-2xl">
                  <img src={story} alt="Company headquarters" className="rounded-lg h-96" />
                </div>
                <div className="absolute -bottom-8 -left-8 bg-white text-black p-4 rounded-lg shadow-lg">
                  <div className="flex items-center gap-2">
                    <Award className="text-yellow-600" />
                    <span className="font-bold">Industry Leaders</span>
                  </div>
                </div>
                <div className="absolute -top-8 -right-8 bg-white text-black p-4 rounded-lg shadow-lg">
                  <div className="flex items-center gap-2">
                    <Medal className="text-yellow-600" />
                    <span className="font-bold">Excellence</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Our Story Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="md:w-1/2">
                <div className="relative">
                  <img src={about} alt="Company founders" className="rounded-lg shadow-lg h-1/4" />
                  
                </div>
              </div>
              <div className="md:w-1/2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
                <p className="text-gray-700 mb-4">
                  PVG Import Export was by a team of industry veterans with a vision to create a more transparent, efficient, and reliable precious metals trading company. What began as a small operation has grown into a global enterprise serving clients.
                </p>
                <p className="text-gray-700 mb-4">
                  Throughout our journey, we've remained committed to our founding principles: integrity in every transaction, excellence in service delivery, and innovation in our approach to the precious metals market.
                </p>
               
                
              </div>
            </div>
          </div>
        </section>
        
        {/* Our Values Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                These principles guide every aspect of our business, from client interactions to internal operations.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Value 1 */}
              <div className=" p-6 rounded-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Shield className="text-yellow-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Integrity</h3>
                <p className="text-gray-600 text-center">
                  We uphold the highest ethical standards in all our dealings, ensuring transparency and honesty in every transaction.
                </p>
              </div>
              
              {/* Value 2 */}
              <div className=" p-6 rounded-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Award className="text-yellow-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Excellence</h3>
                <p className="text-gray-600 text-center">
                  We strive for excellence in everything we do, from the quality of our products to the service we provide our clients.
                </p>
              </div>
              
              {/* Value 3 */}
              <div className=" p-6 rounded-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Globe className="text-yellow-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Global Perspective</h3>
                <p className="text-gray-600 text-center">
                  We embrace diversity of thought and experience, leveraging our global presence to better serve our international clientele.
                </p>
              </div>
              
              {/* Value 4 */}
              <div className=" p-6 rounded-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Users className="text-yellow-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Client Focus</h3>
                <p className="text-gray-600 text-center">
                  We put our clients at the center of everything we do, tailoring our services to meet their unique needs and exceed expectations.
                </p>
              </div>
              
              {/* Value 5 */}
              <div className=" p-6 rounded-lg  hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Briefcase className="text-yellow-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Professionalism</h3>
                <p className="text-gray-600 text-center">
                  We maintain the highest standards of professionalism in our operations, communications, and business relationships.
                </p>
              </div>
              
              {/* Value 6 */}
              <div className=" p-6 rounded-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <MapPin className="text-yellow-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Reliability</h3>
                <p className="text-gray-600 text-center">
                  We deliver on our promises consistently, building lasting relationships based on dependability and trust.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Our Expertise Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="md:w-1/2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Expertise</h2>
                <p className="text-gray-700 mb-4">
                  With decades of combined experience in the precious metals industry, our team brings unparalleled knowledge and expertise to every client relationship.
                </p>
                <p className="text-gray-700 mb-4">
                  We specialize in facilitating seamless cross-border transactions, navigating complex regulatory environments, and providing strategic market insights that give our clients a competitive edge.
                </p>
                
                <div className="mt-8 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Globe className="text-yellow-600" size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Global Trading</h3>
                      <p className="text-gray-600">
                        Expertise in international markets, customs regulations, and cross-border logistics.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Shield className="text-yellow-600" size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Compliance & Security</h3>
                      <p className="text-gray-600">
                        Comprehensive knowledge of regulatory requirements and security protocols for precious metals.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Award className="text-yellow-600" size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Quality Assurance</h3>
                      <p className="text-gray-600">
                        Rigorous testing and verification procedures to ensure the highest product standards.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="md:w-1/2">
                <div className="grid grid-cols-2 gap-4">
                  <img src={trading} alt="Trading floor" className="rounded-lg shadow-md" />
                  <img src={shipping} alt="Quality testing" className="rounded-lg shadow-md" />
                  <img src={quality} alt="Shipping and logistics" className="rounded-lg shadow-md" />
                  <img src={story} alt="Client meeting" className="rounded-lg shadow-md" />
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Leadership Team Section */}
    
        
        {/* Global Presence Section */}
    
        
        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-8 md:mb-0 md:w-2/3">
                <h2 className="text-3xl font-bold mb-4">Partner With PVG Import Export</h2>
                <p className="text-yellow-100 max-w-xl">
                  Experience the difference of working with the industry's most trusted precious metals trading partner. Contact us today to discuss your specific requirements.
                </p>
              </div>
              <div>
                <Link to='/contact'>
                <button className="bg-white text-yellow-600 hover:bg-gray-100 font-bold py-3 px-6 rounded shadow-lg transition-colors">
                  Schedule a Consultation
                </button>
                </Link>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default AboutUs;